import React, { useState } from "react";
import { Card, Spin } from "antd";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { getUserInfo } from "redux/selectors";
import { useHistory } from "react-router-dom";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const SectionsRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const StyledTaskContainer = styled.div`
  flex: 1;
  min-width: 0;
  padding: 5px;
  box-shadow: 0px 4px 30px 0px #0000001f;
  border-radius: 8px;
`;

const StyledLogContainer = styled.div`
  flex: 1;
  min-width: 0;
  padding: 15px;
  box-shadow: 0px 4px 30px 0px #0000001f;
  border-radius: 8px;
`;

// ✅ LogTopRow — flex row, children sit side by side, left-aligned so both
// donuts land at the same x-position regardless of label text length
const LogTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  width: 100%;
`;

// ✅ LabelBlock — fixed width so "Total TimeLogs" and "Total Claims"
// (different text widths) don't push their donuts to different offsets
const LabelBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;
  width: 70px;
  flex-shrink: 0;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTaskTitle = styled.h6`
  font-size: 16px;
  font-weight: 700;
  color: #1f243b;
  margin: 0 0 30px 0;
`;

const SectionLogTitle = styled.h6`
  font-size: 16px;
  font-weight: 700;
  color: #1f243b;
  margin: 0 0 0px 0;
`;

const TaskCardsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const TaskCard = styled.div`
  flex: 1;
  min-width: 120px;
  background: #eef2ff;
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
  transition: background 0.18s, box-shadow 0.18s, transform 0.15s;

  &:hover {
    background: #e0e7ff;
    box-shadow: 0 4px 14px rgba(99, 102, 241, 0.15);
    transform: translateY(-2px);
  }

  .task-label {
    font-size: 16px;
    color: #636980;
    font-weight: 400;
    margin-bottom: 6px;
  }

  .task-count {
    font-size: 18px;
    font-weight: 700;
    color: #1f243b;
  }
`;

const LegendRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  font-size: 14px;
  color: #1f243b;

  .legend-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  .legend-value {
    flex: 1;
    text-align: right;
    white-space: nowrap;
  }
`;

const DotSq = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
  background: ${({ color }) => color};
`;

const LogsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
`;

const LogCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 14px;
  padding: 12px 16px 12px 0;

  &:first-child {
    padding-left: 0; /* flush with the "Logs Report" title above */
    padding-right: 16px;
    border-right: 1px solid #f3f4f6;
  }

  &:last-child {
    padding-left: 16px;
  }
`;

const LogLabel = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #1f243b;
  line-height: 1.4;
  text-align: left;
`;

// ✅ Symmetric padding (was 20px right-only, which skewed the two columns'
// effective widths relative to each other)
const LegendBlock = styled.div`
  width: 100%;
  min-width: 180px; /* ensures time strings like "3Hr 00Min 00Sec" fit on one line */
  padding: 5px 10px 0 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DonutWrap = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  flex-shrink: 0;
  svg { transform: rotate(-90deg); }
  .center-label {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 400;
    color: #1F243B;
    text-align: center;
    padding: 0 6px;
    line-height: 1.3;
  }
`;

const CurrencyNav = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NavArrow = styled.button`
  background: none;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  color: #5668e2;
  font-size: 11px;
  border-radius: 4px;
  transition: background 0.15s;
  display: flex;
  align-items: center;
  line-height: 1;

  &:hover { background: #eef2ff; }
  &:disabled { color: #c7cadd; cursor: default; background: none; }
`;

const CurrencyLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #5668e2;
  min-width: 34px;
  text-align: center;
  letter-spacing: 0.4px;
`;

// ── Helpers ─────────────────────────────────────────────────────────────────

// Parse "0hr 1min 39sec" → { hr, min, sec }
function parseTimeParts(raw = "") {
  const hr = parseInt(raw.match(/(\d+)\s*hr/i)?.[1] ?? "0", 10);
  const min = parseInt(raw.match(/(\d+)\s*min/i)?.[1] ?? "0", 10);
  const sec = parseInt(raw.match(/(\d+)\s*sec/i)?.[1] ?? "0", 10);
  return { hr, min, sec };
}

// Total seconds from parts
function toSeconds({ hr, min, sec }) {
  return hr * 3600 + min * 60 + sec;
}

// Format parts → smart string: skip leading zeros
// e.g. {hr:0,min:1,sec:39} → "01Min 39Sec"
//      {hr:1,min:10,sec:5} → "1Hr 10Min 05Sec"
//      {hr:0,min:0,sec:5}  → "05Sec"
function formatTimeParts({ hr, min, sec }) {
  const parts = [];
  if (hr > 0) parts.push(`${hr}Hr`);
  if (min > 0 || hr > 0) parts.push(`${String(min).padStart(2, "0")}Min`);
  parts.push(`${String(sec).padStart(2, "0")}Sec`);
  return parts.join(" ");
}

function formatChartTimeParts({ hr, min, sec }) {
  return [
    String(hr).padStart(2, "0"),
    String(min).padStart(2, "0"),
    String(sec).padStart(2, "0"),
  ].join(":");
}

// Sum two time raw strings → formatted string for donut center
function sumTimeLabel(a = "", b = "") {
  const pa = parseTimeParts(a);
  const pb = parseTimeParts(b);
  const totalSec = toSeconds(pa) + toSeconds(pb);
  const hr = Math.floor(totalSec / 3600);
  const min = Math.floor((totalSec % 3600) / 60);
  const sec = totalSec % 60;
  return formatChartTimeParts({ hr, min, sec });
}

// Decimal hours for donut arc sizing only
function toDecimalHours(raw = "") {
  const { hr, min, sec } = parseTimeParts(raw);
  return hr + min / 60 + sec / 3600;
}

// ────────────────────────────────────────────────────────────────────────────

function Donut({ billed, unbilled, centerLabel, unit = "" }) {
  const total = parseFloat((billed + unbilled).toFixed(4));
  const r = 38;
  const cx = 50;
  const cy = 50;
  const circumference = 2 * Math.PI * r;

  const billedFrac = total > 0 ? billed / total : 0;
  const billedDash = billedFrac * circumference;
  const unbilledDash = circumference - billedDash;

  // Use explicit centerLabel if provided, else build from unit+total
  const displayText = centerLabel ?? `${unit}${total}`;
  const fontSize = displayText.length > 8 ? "10px"
    : displayText.length > 5 ? "11px"
      : "13px";

  return (
    <DonutWrap>
      <svg width="100" height="100" viewBox="0 0 100 100">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e0e7ff" strokeWidth="12" />
        <circle
          cx={cx} cy={cy} r={r} fill="none"
          stroke="#B7C1FF" strokeWidth="12"
          strokeDasharray={`${unbilledDash} ${circumference}`}
          strokeDashoffset={-billedDash}
        />
        <circle
          cx={cx} cy={cy} r={r} fill="none"
          stroke="#5668E2" strokeWidth="12"
          strokeDasharray={`${billedDash} ${circumference}`}
        />
      </svg>
      <span className="center-label" style={{ fontSize }}>
        {displayText}
      </span>
    </DonutWrap>
  );
}

// ────────────────────────────────────────────────────────────────────────────

const Logs = () => {
  const userInfo = useSelector(getUserInfo);
  const history = useHistory();
  const [claimIdx, setClaimIdx] = useState(0);

  const { key, url } = queryKeys.getIndividualSummary(
    `?userid=${userInfo?.userid}`
  );

  const { data: LogRes, isLoading } = useFetch(key, url, {
    enabled: !!userInfo?.userid,
  });

  const summary = LogRes?.response || {};

  const tasksAssigned = summary.tasksAssigned ?? 0;
  const tasksCreated = summary.tasksCreated ?? 0;
  const timelogs = summary.timelogs ?? { billed: "0hr 0min 0sec", unbilled: "0hr 0min 0sec" };
  const claimsArr = summary.claims ?? [];

  // TimeLogs
  const timelogBilledHrs = toDecimalHours(timelogs.billed);
  const timelogUnbilledHrs = toDecimalHours(timelogs.unbilled);
  const timeDonutCenter = sumTimeLabel(timelogs.billed, timelogs.unbilled);

  // Claims — current currency
  const currentClaim = claimsArr[claimIdx] ?? { currency: "—", billed: 0, unbilled: 0 };
  const claimBilled = currentClaim.billed ?? 0;
  const claimUnbilled = currentClaim.unbilled ?? 0;
  const claimCurrency = currentClaim.currency ?? "";

  const currencySymbol = claimCurrency === "USD" ? "$"
    : claimCurrency === "EUR" ? "€"
      : claimCurrency === "GBP" ? "£"
        : claimCurrency;

  const handleTaskNav = () => history.push("/task");

  return (
    <SectionsRow>
      <StyledTaskContainer>
        <Card bordered={false} style={{ borderRadius: 12 }}>
          <Spin spinning={isLoading}>
            <Wrapper>
              <div>
                <SectionTaskTitle>Pending Tasks</SectionTaskTitle>
                <TaskCardsRow>
                  <TaskCard onClick={handleTaskNav} style={{ textAlign: "center" }}>
                    <div className="task-label">Tasks Assigned to You</div>
                    <div className="task-count">{tasksAssigned}</div>
                  </TaskCard>
                  <TaskCard onClick={handleTaskNav} style={{ textAlign: "center" }}>
                    <div className="task-label">Tasks You Created</div>
                    <div className="task-count">{tasksCreated}</div>
                  </TaskCard>
                </TaskCardsRow>
              </div>
            </Wrapper>
          </Spin>
        </Card>
      </StyledTaskContainer>

      <StyledLogContainer>
        <Spin spinning={isLoading}>
          <Wrapper>
            <div>
              <SectionLogTitle style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                Logs Report
                {claimsArr.length > 1 && (
                  <CurrencyNav>
                    <NavArrow onClick={() => setClaimIdx(i => i - 1)} disabled={claimIdx === 0}>
                      <LeftOutlined />
                    </NavArrow>
                    <CurrencyLabel>{claimCurrency}</CurrencyLabel>
                    <NavArrow onClick={() => setClaimIdx(i => i + 1)} disabled={claimIdx === claimsArr.length - 1}>
                      <RightOutlined />
                    </NavArrow>
                  </CurrencyNav>
                )}
              </SectionLogTitle>
              <LogsGrid>
                {/* ── TimeLogs Column ── */}
                <LogCol>
                  <LogTopRow>
                    <LabelBlock>
                      <LogLabel>Total<br />TimeLogs</LogLabel>
                    </LabelBlock>
                    <Donut
                      billed={timelogBilledHrs}
                      unbilled={timelogUnbilledHrs}
                      centerLabel={timeDonutCenter}
                    />
                  </LogTopRow>
                  <LegendBlock>
                    <LegendRow>
                      <div className="legend-left"><DotSq color="#5668E2" /> Billed</div>
                      <span className="legend-value">
                        {timelogBilledHrs > 0 ? formatTimeParts(parseTimeParts(timelogs.billed)) : "0"}
                      </span>
                    </LegendRow>
                    <LegendRow>
                      <div className="legend-left"><DotSq color="#B7C1FF" /> Unbilled</div>
                      <span className="legend-value">
                        {timelogUnbilledHrs > 0 ? formatTimeParts(parseTimeParts(timelogs.unbilled)) : "0"}
                      </span>
                    </LegendRow>
                  </LegendBlock>
                </LogCol>

                {/* ── Claims Column ── */}
                <LogCol>
                  <LogTopRow>
                    <LabelBlock>
                      <LogLabel>Total<br />Claims</LogLabel>
                    </LabelBlock>
                    <Donut
                      billed={claimBilled}
                      unbilled={claimUnbilled}
                      centerLabel={claimBilled + claimUnbilled > 0 ? `${currencySymbol}${claimBilled + claimUnbilled}` : "0"}
                    />
                  </LogTopRow>
                  <LegendBlock>
                    <LegendRow>
                      <div className="legend-left"><DotSq color="#5668E2" /> Billed</div>
                      <span className="legend-value">
                        {claimBilled > 0 ? `${currencySymbol}${claimBilled}` : "0"}
                      </span>
                    </LegendRow>
                    <LegendRow>
                      <div className="legend-left"><DotSq color="#B7C1FF" /> Unbilled</div>
                      <span className="legend-value">
                        {claimUnbilled > 0 ? `${currencySymbol}${claimUnbilled}` : "0"}
                      </span>
                    </LegendRow>
                  </LegendBlock>
                </LogCol>
              </LogsGrid>
            </div>
          </Wrapper>
        </Spin>
      </StyledLogContainer>
    </SectionsRow>
  );
};

export default Logs;